import React, { useCallback, useContext, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CarouselLink, ImagePreset, ModuleType, ProfileType, useCarousel } from '@vodafoneis/sjonvarpskjarni-js-lib';
import { UserContext } from '../../contexts/UserContext';
import MediaCarousel from '../../components/MediaCarousel';
import { CarouselColumn } from '../../components/CarouselColumn';
import { ImageComponent, ImageComponentContainer } from '../../components/ImageComponent';
import { Highlight } from '../../components/Highlight';
import { useDevicetype } from '../../hooks/useDeviceType';
import { GRID_GUTTER_WIDTH } from '../../styles/variables';
import { PROVIDERS_CAROUSEL } from '../../config/constants';
import { getCarouselLinkUrl } from '../../utils/urls';

const { M2, M4 } = ModuleType;

const SQUARE_COLUMNS = { xs: 4, sm: 3, lg: 2 };

const SQUARE_MAX_DPR = 4;

const KIDS_ALLOWED_URLS = ['/genre/children', '/genre/catchup'];

const promoColumnsFor = (moduleType?: ModuleType) => {
	switch (moduleType) {
		case M2:
			return { xs: 12, md: 6 };
		case M4:
			return { xs: 12, md: 6, lg: 3 };
		default:
			return { xs: 12, md: 6, lg: 4 };
	}
};

const TileContent = styled(ImageComponentContainer)`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	border-radius: 6px;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;

	.image {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
	}
`;

const TileLabel = styled.div`
	color: #eee;
	font-weight: 700;
	font-size: 21px;
	text-align: center;
	padding: 0 16px;
`;

const SquareTile = styled.div`
	position: relative;
	cursor: pointer;
	height: 0;
	padding-bottom: 100%;
	margin-bottom: ${GRID_GUTTER_WIDTH};
`;

type ProviderTileProps = {
	link: CarouselLink;
	square: boolean;
	imageUrl: string | null;
	enabled: boolean;
};

const ProviderTile: React.FC<ProviderTileProps> = ({ link, square, imageUrl, enabled }) => {
	const router = useRouter();

	const onClick = useCallback(
		(e) => {
			e.preventDefault();

			if (!enabled) {
				return;
			}

			const url = getCarouselLinkUrl(link.link);
			if (url) {
				router.push(url);
			}
		},
		[enabled, link, router]
	);

	const content = (
		<>
			<TileContent>
				{imageUrl ? <ImageComponent className={'image'} images={[imageUrl]} /> : <TileLabel>{link.name}</TileLabel>}
			</TileContent>
			<Highlight />
		</>
	);

	if (square) {
		return (
			<SquareTile role={'link'} aria-label={link.name} onClick={onClick}>
				{content}
			</SquareTile>
		);
	}

	return (
		<div role={'link'} aria-label={link.name} className={'media-component ad'} onClick={onClick}>
			{content}
		</div>
	);
};

export const FrontpageProviders: React.FC = () => {
	const { t } = useTranslation();
	const { user } = useContext(UserContext);
	const { carousel, links } = useCarousel(PROVIDERS_CAROUSEL);
	const square = useDevicetype() === 'mobile';
	const isKids = user?.selectedProfile?.type === ProfileType.KIDS;

	const items = useMemo(
		() =>
			links.filter((link) => {
				const url = getCarouselLinkUrl(link.link);
				return url !== null && (!isKids || KIDS_ALLOWED_URLS.includes(url));
			}),
		[isKids, links]
	);

	const columns = useMemo(() => (square ? SQUARE_COLUMNS : promoColumnsFor(carousel?.promoModuleType)), [carousel, square]);

	const renderItem = useCallback(
		(link: CarouselLink, clickEnabled: boolean) => {
			// poster-* presets crop the square art to 2:3
			const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
			const imageUrl = square
				? link.getPosterImageUrl({ presets: [ImagePreset.PRODUCT_ICON_SMALL], dpr: Math.min(SQUARE_MAX_DPR, Math.ceil(2 * dpr)), ext: 'png' })
				: link.getPromoImageUrl({ presets: [dpr > 1 ? ImagePreset.AD_MEDIUM : ImagePreset.AD_SMALL], ext: 'png' });

			return (
				<CarouselColumn key={link.id} {...columns}>
					<ProviderTile link={link} square={square} imageUrl={imageUrl} enabled={clickEnabled !== false} />
				</CarouselColumn>
			);
		},
		[columns, square]
	);

	return <MediaCarousel id={'carousel-frontpage-providers'} title={t('Providers.Title')} items={items} renderItem={renderItem} />;
};
